import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  input,
  viewChild,
  viewChildren,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';
import {
  injectVirtualizer,
  ScrollToOptions,
  VirtualItem,
} from '@tanstack/angular-virtual';
import {
  UiDeviceListItemContext,
  UiDeviceListItemTemplate,
} from './ui-device-list.model';

/**
 * Generic, variable-height virtual list for device-oriented views.
 *
 * The component owns only virtualization. Data shaping, expansion state,
 * filtering and row interactions stay in the consuming feature.
 */
@Component({
  selector: 'ui-device-list',
  standalone: true,
  imports: [CdkScrollable, NgTemplateOutlet],
  templateUrl: './ui-device-list.component.html',
  styleUrl: './ui-device-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiDeviceListComponent<T> {
  /** Flat sequence of rows to render. */
  public readonly items = input.required<readonly T[]>();
  /** Stable row identity. This preserves measured sizes when rows are re-rendered. */
  public readonly getItemKey =
    input.required<(item: T, index: number) => string | number>();
  /** Initial height estimate, refined automatically from the rendered DOM. */
  public readonly estimateSize =
    input.required<(item: T, index: number) => number>();
  /** Row template with `$implicit`, `item`, `index` and `virtualItem` context values. */
  public readonly itemTemplate = input.required<UiDeviceListItemTemplate<T>>();
  public readonly overscan = input<number>(10);
  public readonly paddingStart = input<number>(0);
  public readonly paddingEnd = input<number>(0);

  private readonly scrollElement =
    viewChild<ElementRef<HTMLDivElement>>('scrollElement');
  private readonly rowElements =
    viewChildren<ElementRef<HTMLElement>>('virtualRow');

  protected readonly virtualizer = injectVirtualizer(() => ({
    scrollElement: this.scrollElement(),
    count: this.items().length,
    estimateSize: (index: number) => {
      const item = this.items()[index];
      return item ? this.estimateSize()(item, index) : 0;
    },
    getItemKey: (index: number) => {
      const item = this.items()[index];
      return item ? this.getItemKey()(item, index) : index;
    },
    overscan: this.overscan(),
    paddingStart: this.paddingStart(),
    paddingEnd: this.paddingEnd(),
  }));

  constructor() {
    effect(() => {
      this.items();
      this.rowElements().forEach((element) => {
        this.virtualizer.measureElement(element.nativeElement);
      });
    });
  }

  protected itemContext(
    virtualItem: VirtualItem,
  ): UiDeviceListItemContext<T> | null {
    const item = this.items()[virtualItem.index];

    if (!item) {
      return null;
    }

    return { $implicit: item, item, index: virtualItem.index, virtualItem };
  }

  /** Allows a consumer to navigate to a row without exposing TanStack internals. */
  public scrollToIndex(
    index: number,
    behavior: ScrollToOptions['behavior'] = 'auto',
  ): void {
    this.virtualizer.scrollToIndex(index, { behavior });
  }
}
